/**
 * MA Financial brand style configuration — colors, typography, and prompt
 * rules extracted from the client's "MA Financial PPT Slides for 3.0.pptx"
 * (slide master colorScheme "MA Financial" + fontScheme, layout placeholder
 * geometry, via the OOXML).
 *
 * Mirrors the shape of `cavendishStyleConfig.ts` / `evercoreStyleConfig.ts`.
 * When MA Financial style is active the prompt below is appended to the
 * system prompt and the color/theme constants are used as renderer
 * defaults.
 *
 * Fidelity note: theme colors come from the master colorScheme "MA
 * Financial"; fonts from fontScheme "Office Classic 2" (Arial for titles AND
 * body — confirmed by ~600 explicit `<a:latin typeface="Arial"/>` runs across
 * the sample deck, with no other typeface used at meaningful volume). Slide
 * size is A4-landscape (842.0×595.3 pt). Title/content geometry is from the
 * slide master. The logo lives on the slide master, so default AddSlide
 * inherits it.
 *
 * The theme's official accent4 (orange, #FC6C0C) and hlink/folHlink values
 * are recorded for completeness but were not found in active use across the
 * sample deck; the dominant "ink" color used for headings and emphasis
 * blocks throughout the deck is a darker navy (#1E2846) that does not appear
 * in the official colorScheme, so it is called out separately below.
 */

import type { ThemeColors } from "../../types";

// ---------------------------------------------------------------------------
// Color palette (6-digit hex, no '#') — from the "MA Financial" colorScheme
// ---------------------------------------------------------------------------

export const MA_FINANCIAL_COLORS = {
  black: "000000", // dk1 — Text1 (theme default)
  darkGray: "1F1F1F", // dk2 — Text2 (theme default)
  white: "FFFFFF", // lt1
  lightTaupe: "DED8D2", // lt2
  blue: "2D3CB4", // accent1 — MA Financial signature blue (primary brand / emphasis)
  lightBlue: "8ACFFF", // accent2
  taupe: "AEA8A2", // accent3
  orange: "FC6C0C", // accent4 (present in theme, not seen in active use)
  brightBlue: "0096FF", // accent5
  teal: "1E666C", // accent6
  maroon: "961E4E", // folHlink

  // Practical "ink" navy used throughout the sample deck for headings and
  // emphasis text blocks — far more common in practice than the theme's
  // official dk1/dk2, but not itself a named theme color.
  ink: "1E2846",
  // Light band used for table row shading / background panels across slides.
  altRow: "E1E6EB",
} as const;

/**
 * Drop-in ThemeColors override when MA Financial style is active. Values map
 * 1:1 to the MA Financial slide-master colorScheme + fontScheme, EXCEPT
 * dark2 which uses the practical ink navy (#1E2846) actually used for
 * headings/emphasis throughout the deck rather than the barely-used
 * official dk2 (#1F1F1F) — see fidelity note above.
 */
export const MA_FINANCIAL_THEME_COLORS: ThemeColors = {
  dark1: MA_FINANCIAL_COLORS.black, // #000000 — Text1
  dark2: MA_FINANCIAL_COLORS.ink, // #1E2846 — practical heading/emphasis ink
  light1: MA_FINANCIAL_COLORS.white, // #FFFFFF — Background1
  light2: MA_FINANCIAL_COLORS.lightTaupe, // #DED8D2 — Background2
  accent1: MA_FINANCIAL_COLORS.blue, // #2D3CB4
  accent2: MA_FINANCIAL_COLORS.lightBlue, // #8ACFFF
  accent3: MA_FINANCIAL_COLORS.taupe, // #AEA8A2
  accent4: MA_FINANCIAL_COLORS.orange, // #FC6C0C
  accent5: MA_FINANCIAL_COLORS.brightBlue, // #0096FF
  accent6: MA_FINANCIAL_COLORS.teal, // #1E666C
  hyperlink: MA_FINANCIAL_COLORS.blue,
  followedHyperlink: MA_FINANCIAL_COLORS.maroon,
  majorFont: "Arial",
  minorFont: "Arial",
};

/**
 * Ordered series palette. Led by the MA Financial blue (accent1), followed
 * by colors actually observed in charts/tables across the sample deck
 * (taupe, bright blue, teal, light blue). The theme's orange accent4 is
 * intentionally NOT first since it was not found in active use.
 */
export const MA_FINANCIAL_CHART_SERIES_PALETTE = [
  MA_FINANCIAL_COLORS.blue,
  MA_FINANCIAL_COLORS.taupe,
  MA_FINANCIAL_COLORS.brightBlue,
  MA_FINANCIAL_COLORS.teal,
  MA_FINANCIAL_COLORS.lightBlue,
];

// ---------------------------------------------------------------------------
// Slide dimensions & placeholder positions (from the MA Financial master)
// ---------------------------------------------------------------------------

export const MA_FINANCIAL_SLIDE_DIMENSIONS = { width: 842.0, height: 595.3 } as const;

export const MA_FINANCIAL_POSITIONS = {
  title: { left: 35.4, top: 31.1, width: 680.3, height: 24.2 },
  contentStart: { left: 35.4, top: 106.5 },
  footnote: { left: 555.9, top: 11.3, width: 250.6, height: 8.5 }, // date field, top-right
} as const;

// Authoritative layout names from the MA Financial master.
export const MA_FINANCIAL_LAYOUT_NAMES = [
  "Divider Navy",
  "Divider Blue",
  "Divider Grey",
  "Divider White",
  "Quote Navy",
  "Quote Blue",
  "Quote Grey",
  "Quote White",
  "Table of Contents",
  "Table of Contents White",
  "Feature Title & Content",
  "Title and Content",
  "Title & Content (2 column)",
  "Two Content Layout Horizontal",
  "Two Content",
  "Two Content Subheadings",
  "Two Content Pullout",
  "Two Content Pullout (white text)",
  "Two Content Feature",
  "Content & One Third Picture",
  "Content & Half Picture",
  "Content & Picture",
  "Content & 2 Pictures",
  "One Third",
  "One Third Subheading",
  "Two Third",
  "Two Third Subheading",
  "Three Content",
  "Three Content Subheadings",
  "One Large Two Small Horiz Content",
  "One Large Two Small Vert Content",
  "Two Small Horiz One Large Content",
  "Two Small Vert One Large Content",
  "Four Content",
  "Four Content Subheadings",
  "Four Content Grid",
  "Four Content Grid Subheadings",
  "Six Content Grid",
  "Six Content Grid Subheadings",
  "Four Content Pullouts",
  "Two Content Subheading Pullouts",
  "Three Content Subheading Pullouts",
  "Four Content Subheading Pullouts",
  "Picture with Caption",
  "Full Slide Picture (White Logo)",
  "Title Only",
  "Disclaimer",
  "Blank",
  "Title Image",
  "1_Title and Content",
] as const;

// ---------------------------------------------------------------------------
// Prompt text — appended to the system prompt when MA Financial style is active
// ---------------------------------------------------------------------------

export const MA_FINANCIAL_STYLE_PROMPT = `

## MA FINANCIAL BRAND STYLE OVERRIDE (active — apply these rules to ALL output)

CRITICAL FONT RULE: You MUST include \`fontFamily\` on EVERY shape you create or modify — even when editing existing text. This OVERRIDES the general rule about matching the existing presentation's fonts. MA Financial uses a single Latin typeface throughout:
- Titles, headings, body, bullets, tables, charts: \`"Arial"\`
NEVER omit fontFamily when MA Financial style is active. If a shape already has the wrong font, include fontFamily to correct it.

### Color palette (from the MA Financial master colorScheme)
Use ONLY these MA Financial brand colors. Do NOT use arbitrary hex values.

**Theme mapping:**
- Text1 / dark1: Black #${MA_FINANCIAL_COLORS.black}
- Text2 / dark2 (heading & emphasis ink): Navy #${MA_FINANCIAL_COLORS.ink}
- Background1 / light1: White #${MA_FINANCIAL_COLORS.white}
- Background2 / light2: Light taupe #${MA_FINANCIAL_COLORS.lightTaupe}
- Accent1: Blue #${MA_FINANCIAL_COLORS.blue}
- Accent2: Light blue #${MA_FINANCIAL_COLORS.lightBlue}
- Accent3: Taupe #${MA_FINANCIAL_COLORS.taupe}
- Accent4: Orange #${MA_FINANCIAL_COLORS.orange} (rarely used — prefer other accents)
- Accent5: Bright blue #${MA_FINANCIAL_COLORS.brightBlue}
- Accent6: Teal #${MA_FINANCIAL_COLORS.teal}

**Usage:**
- **Primary brand / emphasis**: Blue #${MA_FINANCIAL_COLORS.blue}
- **Headings & emphasis text**: Navy ink #${MA_FINANCIAL_COLORS.ink}
- **Body text**: Black #${MA_FINANCIAL_COLORS.black}
- **Background / row shading**: Light band #${MA_FINANCIAL_COLORS.altRow}
- **Chart series palette** (in order): ${MA_FINANCIAL_CHART_SERIES_PALETTE.map((c) => `#${c}`).join(", ")}
  Lead with blue (#${MA_FINANCIAL_COLORS.blue}) as the first/primary series color.

### Typography (authoritative — from the MA Financial master)
Slide dimensions: 842.0 × 595.3 pt (A4 landscape).

**Placeholder positions:**
- **Title**: left=35.4, top=31.1, width=680.3, height=24.2 — "Arial" 20pt, blue (#${MA_FINANCIAL_COLORS.blue}), left-aligned
- **Content area**: starts at left=35.4, top=106.5
- **Slide number**: bottom-right — "Arial" 8pt, blue (#${MA_FINANCIAL_COLORS.blue})

**Content text hierarchy (from the master — "Arial", black #${MA_FINANCIAL_COLORS.black}, ~10pt body):**
- Keep body text ~10pt; sub-levels smaller. Sentence case, single-spaced, left-justified.
- For an emphasized section heading, use the navy ink (#${MA_FINANCIAL_COLORS.ink}) or bold; bullets otherwise inherit black text.

### Charts (MA Financial style)
- Font: "Arial", minimum 8pt
- **Primary data color**: blue (#${MA_FINANCIAL_COLORS.blue}); multi-series uses the series palette above in order
- Data labels: positioned outside bars/columns (inside for stacked)
- Gridlines: minimal — none by default; only when no data labels are shown
- Legend: bottom-center placement (only when needed for multi-series)
- No 3D effects, gradients, or graphic fills

### Tables (MA Financial style)
- Header row: navy ink (#${MA_FINANCIAL_COLORS.ink}) fill with white text, bold
- Body: white fill, black "Arial" text (10pt)
- Alternate-row shading: light band (#${MA_FINANCIAL_COLORS.altRow})
- Borders: thin taupe (#${MA_FINANCIAL_COLORS.taupe}); align numeric columns consistently

### Shapes & layout (MA Financial style)
- Separate content with SHARP rectangular blocks — NOT rounded shapes
- Use thin taupe (#${MA_FINANCIAL_COLORS.taupe}) rule lines for separation when needed
- Connectors: solid taupe (#${MA_FINANCIAL_COLORS.taupe}) lines
- Page numbers / footer: ~8pt, blue, "Arial"

**TEXT FITTING RULE**: Keep labels and item descriptions SHORT (max 6-8 words each). Text boxes are narrow; long descriptions overflow. Use concise labels, not full sentences.
`;
